import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/shadcn/ui/button';
import { deleteSignalMutationOptions } from '@/entities/signal';
import { captureException } from '@/shared/lib/sentry';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/stores/pure/ToastStore';

export type DeleteSignalDialogProps = {
  signalId: string;
  alertNames: string[];
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
};

export function DeleteSignalDialog({
  signalId,
  alertNames,
  isOpen,
  close,
  unmount,
}: DeleteSignalDialogProps) {
  const [_, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation(
    deleteSignalMutationOptions(queryClient),
  );

  const description =
    alertNames.length > 0
      ? `Deleting this signal type will also remove alerts: '${alertNames.join("' and '")}'. Edit the alerts first if you want to keep them.`
      : 'This action cannot be undone. Are you sure you want to proceed?';

  const handleDelete = async () => {
    try {
      await mutateAsync({ signal_id: signalId });
      toast('Signal type deleted', 'success', undefined, { body: 'Your signal type has been deleted successfully.' });
      setSearchParams({ signal: 'all' });
    } catch (error) {
      toast('Failed to delete signal', 'fail');
      captureException(error);
    }
    close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => close()} onExitComplete={unmount}>
      <DialogContent size="s">
        <DialogHeader>
          <DialogTitle>Are you sure you want to delete?</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div />
          <div className="flex gap-2">
            <DialogClose>
              <Button variant="outline" onClick={() => close()}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isPending}
            >
              {isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
